// ============================================
// Context Layer Mode Detection Engine
// ============================================
//
// Determines conversation modes based on the
// patient's 17-variable ContextLayer. Modes are
// non-exclusive — a patient can be in multiple
// modes simultaneously (e.g., high_empathy +
// budget_conscious + simplified).
// ============================================

import type { ContextLayer, ConversationMode, CheckItem, RegisterType, NursePhase } from '../../types/patientJourney';
import { DEFAULT_CONTEXT } from '../../types/patientJourney';

export { DEFAULT_CONTEXT };

export function detectConversationModes(ctx: ContextLayer): ConversationMode[] {
  const modes: ConversationMode[] = [];

  // high_empathy: stress = high/crisis OR recentLifeEvent !== 'none' OR timeOfDay = 'late_night'
  if (
    ctx.baselineStress === 'high' ||
    ctx.baselineStress === 'crisis' ||
    ctx.recentLifeEvent !== 'none' ||
    ctx.timeOfDay === 'late_night'
  ) {
    modes.push('high_empathy');
  }

  // simplified: healthKnowledge = minimal/basic AND supplementExperience = 'none'
  if (
    (ctx.healthKnowledge === 'minimal' || ctx.healthKnowledge === 'basic') &&
    ctx.supplementExperience === 'none'
  ) {
    modes.push('simplified');
  }

  // evidence: healthKnowledge = 'expert' OR motivation = 'skeptical' OR aiComfort = 'distrustful'
  if (
    ctx.healthKnowledge === 'expert' ||
    ctx.motivation === 'skeptical' ||
    ctx.aiComfort === 'distrustful'
  ) {
    modes.push('evidence');
  }

  // budget_conscious: budgetSensitivity = high/severe
  if (ctx.budgetSensitivity === 'high' || ctx.budgetSensitivity === 'severe') {
    modes.push('budget_conscious');
  }

  // continuity: journeyStage !== 'first_visit'
  if (ctx.journeyStage !== 'first_visit') {
    modes.push('continuity');
  }

  return modes;
}

/**
 * Generate additional validation checks based on active conversation modes.
 * These context-specific checks are merged with each persona's static
 * checklist before the twin scoring engine runs.
 */
export function generateContextChecks(modes: string[]): CheckItem[] {
  const checks: CheckItem[] = [];

  if (modes.includes('high_empathy')) {
    checks.push(
      { checkId: 'CTX-EMP-01', check: 'Nurse uses empathetic tone and validates patient feelings', category: 'conversational', severity: 'major', automatable: true },
      { checkId: 'CTX-EMP-02', check: 'Nurse offers to continue conversation later if patient seems overwhelmed', category: 'conversational', severity: 'minor', automatable: true },
      { checkId: 'CTX-EMP-03', check: 'Nurse reduces decision load - does not present too many options at once', category: 'conversational', severity: 'major', automatable: true },
    );
  }

  if (modes.includes('simplified')) {
    checks.push(
      { checkId: 'CTX-SIM-01', check: 'Nurse avoids medical jargon or explains terms when used', category: 'conversational', severity: 'major', automatable: true },
      { checkId: 'CTX-SIM-02', check: 'Nurse uses analogies to explain complex concepts', category: 'conversational', severity: 'minor', automatable: true },
      { checkId: 'CTX-SIM-03', check: 'Nurse explains concepts before referencing them in recommendations', category: 'conversational', severity: 'major', automatable: true },
    );
  }

  if (modes.includes('evidence')) {
    checks.push(
      { checkId: 'CTX-EVI-01', check: 'Nurse provides evidence or citations when making clinical claims', category: 'clinical', severity: 'major', automatable: true },
      { checkId: 'CTX-EVI-02', check: 'Nurse acknowledges limitations of recommendations', category: 'conversational', severity: 'minor', automatable: true },
      { checkId: 'CTX-EVI-03', check: 'Nurse presents evidence grades or confidence levels', category: 'clinical', severity: 'minor', automatable: true },
    );
  }

  if (modes.includes('budget_conscious')) {
    checks.push(
      { checkId: 'CTX-BUD-01', check: 'Nurse presents lowest cost tier first before premium options', category: 'pricing', severity: 'major', automatable: true },
      { checkId: 'CTX-BUD-02', check: 'Nurse mentions HSA/FSA eligibility if applicable', category: 'pricing', severity: 'minor', automatable: true },
      { checkId: 'CTX-BUD-03', check: 'Nurse frames cost per day rather than per month where possible', category: 'pricing', severity: 'minor', automatable: true },
    );
  }

  if (modes.includes('continuity')) {
    checks.push(
      { checkId: 'CTX-CON-01', check: 'Nurse references previous interactions or history', category: 'conversational', severity: 'major', automatable: true },
      { checkId: 'CTX-CON-02', check: 'Nurse compares current results to historical data', category: 'clinical', severity: 'major', automatable: true },
      { checkId: 'CTX-CON-03', check: 'Nurse skips unnecessary re-introductions', category: 'conversational', severity: 'minor', automatable: true },
    );
  }

  return checks;
}

export function getContextDescription(ctx: ContextLayer): string {
  const parts: string[] = [];

  parts.push(`${ctx.timeOfDay} session`);

  if (ctx.cyclePhase !== 'not_applicable') {
    parts.push(`${ctx.cyclePhase} phase`);
  }

  parts.push(`${ctx.season} season`);

  if (ctx.timeSinceLastInteraction === 'first_visit') {
    parts.push('first visit');
  } else {
    parts.push(`last interaction: ${ctx.timeSinceLastInteraction} ago`);
  }

  parts.push(`stress: ${ctx.baselineStress}`);

  if (ctx.recentLifeEvent !== 'none') {
    parts.push(`recent event: ${ctx.recentLifeEvent}`);
  }

  parts.push(`motivation: ${ctx.motivation}`);
  parts.push(`${ctx.partnerStatus}, social support: ${ctx.socialSupport}`);

  if (ctx.caregiverBurden !== 'none') {
    parts.push(`caregiver burden: ${ctx.caregiverBurden}`);
  }

  parts.push(`budget sensitivity: ${ctx.budgetSensitivity}`);
  parts.push(`insurance: ${ctx.insuranceContext}`);
  parts.push(`health knowledge: ${ctx.healthKnowledge}`);
  parts.push(`supplement experience: ${ctx.supplementExperience}`);
  parts.push(`AI comfort: ${ctx.aiComfort}`);
  parts.push(`journey stage: ${ctx.journeyStage}`);
  parts.push(`adherence: ${ctx.adherenceLevel}`);

  return parts.join(' | ');
}

export function randomizeContext(seed?: number): ContextLayer {
  const rng = createRng(seed);

  function pick<T>(arr: T[]): T {
    return arr[Math.floor(rng() * arr.length)];
  }

  return {
    timeOfDay: pick(['morning', 'afternoon', 'evening', 'late_night']),
    cyclePhase: pick([
      'follicular', 'ovulatory', 'luteal', 'menstrual',
      'postmenopausal', 'not_applicable',
    ]),
    season: pick(['spring', 'summer', 'fall', 'winter']),
    timeSinceLastInteraction: pick(['first_visit', 'days', 'weeks', 'months']),
    baselineStress: pick(['low', 'moderate', 'high', 'crisis']),
    recentLifeEvent: pick([
      'none', 'divorce', 'job_loss', 'bereavement',
      'new_diagnosis', 'new_baby', 'relocation',
    ]),
    motivation: pick(['desperate', 'concerned', 'curious', 'proactive', 'skeptical']),
    partnerStatus: pick(['single', 'partnered', 'divorced', 'widowed']),
    caregiverBurden: pick(['none', 'light', 'heavy']),
    socialSupport: pick(['strong', 'moderate', 'isolated']),
    budgetSensitivity: pick(['low', 'moderate', 'high', 'severe']),
    insuranceContext: pick(['insured', 'uninsured', 'hsa_fsa_eligible']),
    healthKnowledge: pick(['minimal', 'basic', 'informed', 'expert']),
    supplementExperience: pick(['none', 'some', 'extensive']),
    aiComfort: pick(['distrustful', 'cautious', 'neutral', 'comfortable']),
    journeyStage: pick([
      'first_visit', 'on_protocol', 'retesting',
      'post_escalation', 're_engagement',
    ]),
    adherenceLevel: pick(['high', 'moderate', 'low', 'unknown']),
  };
}

function createRng(seed?: number): () => number {
  let s = seed ?? Math.floor(Math.random() * 2147483647);
  return function () {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ============================================
// v3.1 Bifurcated Persona — Check Generator
// ============================================
//
// Generates validation checks for the v3.1
// two-register nurse system: behavioral constraints,
// register-specific tone checks, and modifier
// flow checks. Does NOT replace generateContextChecks()
// which remains in use for the v2 flow.
// ============================================

export function generateV3Checks(
  register: RegisterType,
  _phase: NursePhase,
  hasLateReveals: boolean,
): CheckItem[] {
  const checks: CheckItem[] = [];

  // ------------------------------------------
  // 6 Behavioral Constraint Checks (always)
  // ------------------------------------------

  checks.push(
    {
      checkId: 'V3-VIS-01',
      check: 'Nurse references at least 3 specific biomarker values by name, number, and status during the conversation',
      category: 'constraint',
      severity: 'major',
      automatable: true,
    },
    {
      checkId: 'V3-ATT-01',
      check: "Nurse attributes recommendations to the Analyst/analysis engine, not personal opinion. Uses phrases like 'based on your analysis' not 'I recommend'",
      category: 'constraint',
      severity: 'major',
      automatable: true,
    },
    {
      checkId: 'V3-FRI-01',
      check: 'Nurse uses explicit transition language before mentioning any products or protocols. No product mentions before clinical context is established.',
      category: 'constraint',
      severity: 'major',
      automatable: true,
    },
    {
      checkId: 'V3-VER-01',
      check: 'Before presenting protocol, nurse asks one question requiring the patient to reference a specific value from their results',
      category: 'constraint',
      severity: 'minor',
      automatable: true,
    },
    {
      checkId: 'V3-PRE-01',
      check: 'By conversation end, clinical justification is established and patient can articulate their own case',
      category: 'constraint',
      severity: 'major',
      automatable: true,
    },
    {
      checkId: 'V3-SEN-01',
      check: 'If patient sentiment deviated >30% negative from baseline, nurse responds with increased empathy (Guide register behavior)',
      category: 'constraint',
      severity: 'critical',
      automatable: true,
    },
  );

  // ------------------------------------------
  // Register-Specific Checks
  // ------------------------------------------

  if (register === 'analyst') {
    checks.push(
      {
        checkId: 'V3-REG-AN-01',
        check: "Analyst register: No emotional language ('I feel', 'I'm sorry')",
        category: 'register',
        severity: 'major',
        automatable: true,
      },
      {
        checkId: 'V3-REG-AN-02',
        check: 'Analyst register: References at least 3 specific biomarker values with actual numbers',
        category: 'register',
        severity: 'major',
        automatable: true,
      },
      {
        checkId: 'V3-REG-AN-03',
        check: 'Analyst register: Uses structured/quantitative language',
        category: 'register',
        severity: 'major',
        automatable: true,
      },
    );
  }

  if (register === 'guide') {
    checks.push(
      {
        checkId: 'V3-REG-GU-01',
        check: 'Guide register: No raw data tables or clinical jargon without explanation',
        category: 'register',
        severity: 'major',
        automatable: true,
      },
      {
        checkId: 'V3-REG-GU-02',
        check: 'Guide register: Translates data into felt experience',
        category: 'register',
        severity: 'major',
        automatable: true,
      },
      {
        checkId: 'V3-REG-GU-03',
        check: 'Guide register: Offers clear, achievable next step',
        category: 'register',
        severity: 'major',
        automatable: true,
      },
    );
  }

  if (register === 'blended') {
    checks.push(
      {
        checkId: 'V3-REG-BL-01',
        check: 'Blended register: Both data precision AND emotional anchoring present',
        category: 'register',
        severity: 'major',
        automatable: true,
      },
      {
        checkId: 'V3-REG-BL-02',
        check: "Blended register: Biomarker values connected to patient's experience or goals",
        category: 'register',
        severity: 'major',
        automatable: true,
      },
    );
  }

  // ------------------------------------------
  // Modifier Flow Checks (conditional)
  // ------------------------------------------

  checks.push({
    checkId: 'V3-MOD-01',
    check: 'Nurse asks about medications before making recommendations',
    category: 'modifier',
    severity: 'major',
    automatable: true,
  });

  if (hasLateReveals) {
    checks.push(
      {
        checkId: 'V3-MOD-02',
        check: 'Late Reveal: Nurse acknowledged new information and updated protocol',
        category: 'modifier',
        severity: 'major',
        automatable: true,
      },
      {
        checkId: 'V3-MOD-03',
        check: "Late Reveal: Nurse used transition language ('let me update your protocol')",
        category: 'modifier',
        severity: 'minor',
        automatable: true,
      },
    );
  }

  return checks;
}
